import { ENDPOINT } from '~/constants/endpoints';

import { type KyRequestOptions } from '@vestigas/data';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import {
  type CustomFieldsBulkQueryParameters,
  type CustomFieldsBulkResponse,
} from './types';
import { zodCustomFieldsBulkPayloadSchema } from './validations';

/**
 * Fetches multiple custom fields in bulk from the API.
 * @param queryParameters - Query parameters to fetch.
 * @returns Array of custom field data.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Custom-Field/operation/query_custom_fields_in_bulk_vadmin_custom_field_query_bulk_post
 */
export const fetchCustomFieldsBulkAdmin = async (
  queryParameters: CustomFieldsBulkQueryParameters,
  options?: KyRequestOptions,
) => {
  try {
    // Some ids will be removed by the validation, so we need to check the length of the ids array after parsing
    if (!zodCustomFieldsBulkPayloadSchema.parse(queryParameters)?.ids?.length) {
      return [];
    }

    const response = await vestigasApi
      .post(ENDPOINT.CUSTOM_FIELD.GET_BULK_ADMIN(), {
        json: queryParameters,
        validate: { body: zodCustomFieldsBulkPayloadSchema },
        ...options,
      })
      .json<CustomFieldsBulkResponse>();

    return response?.items ?? [];
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      // Re-throw AbortError without logging as it's expected when requests are cancelled
      throw error;
    }

    log.error({
      message: 'Error fetching Custom Fields Bulk Admin',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 422: {
          errorMessage = i18n.t(($) => $.customField.getBulk.error422, {
            ns: 'data',
          });
          break;
        }

        case 429: {
          errorMessage = i18n.t(($) => $.customField.getBulk.error429, {
            ns: 'data',
          });
          break;
        }

        default: {
          errorMessage = i18n.t(($) => $.customField.getBulk.error.generic, {
            ns: 'data',
          });
          break;
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
